import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useDispatch, useSelector } from 'react-redux';
import { NavigationService } from '../services/navigation.service';

import { ClientService } from '../services/client.service';

import usePersistantState from 'hooks/state.hook';

import { Client, RootState, ClientFilter, CommonUtilsService } from '@umanforge/common';

import { HomeTopBar } from '../components/menu/home_top_bar.component';
import { ClientComponent } from '../components/clients/client.component';
import { Button } from '../components/inputs/button.component';
import { Loading } from '../components/loading.component';
import { SearchInput } from '../components/inputs/search_input.component';
import { HomeLeftBar } from 'components/menu/home_left_bar.component';

import { PlusIcon } from '../components/icons/plus_icon.component';
import { ClientsIcon } from '../components/icons/clients_icon.component';


export function ClientsView() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );

  /*----------------------------- useState ------------------------------*/
  const [ clients, setClients ] = useState<Client[]>( [] );

  const [ filterName, setFilterName ] = usePersistantState<string>( 'client_filterName', '' );

  const [ isLoading, setIsLoading ] = useState<boolean>( false );
  const [ isFirstRender, setIsFirstRender ] = useState<boolean>( true );

  /*---------------------------- useEffect ------------------------------*/
  useEffect( () => {
    setIsFirstRender( false );
  }, [] );

  useEffect( () => {
    if ( !isFirstRender ) {
      refreshClients();
    }
  }, [ isFirstRender, filterName ] );

  /*---------------------------- Functions ------------------------------*/
  const refreshClients = async (): Promise<void> => {
    setIsLoading( true );
    const filters: ClientFilter = {
      filterName: filterName && filterName != '' ? filterName : undefined
    };
    const response = await ClientService.getAllowedClients( filters, token, dispatch );
    if ( response != null ) {
      CommonUtilsService.sortByKey( response, 'name', 'asc' );
      setClients( response );
    }
    setTimeout(  () => { setIsLoading( false ); }, 75 );
  };


  /*------------------------------- Front -------------------------------*/
  return (
    <>
      <HomeLeftBar items={ NavigationService.getNavBarItems( token, dispatch ) } needConfirmation={ false } />
      <div>
        <HomeTopBar title={ t( 'Client' ) } needConfirmationToNav={ false }/>
        <div className='flex gap-1 h-full'>
          <div className='flex flex-col w-full h-full mx-2 items-center'>
            {
              !isFirstRender ?
                <div className='flex w-1/2 min-w-[400px] gap-2 justify-center items-center'>
                  <SearchInput
                    value={ filterName }
                    onSearched={ value => { setFilterName( value ); } }
                    t={ t }
                  />
                </div>
                :
                null
            }
            {
              me?.isSuperAdmin ?
                <div className='w-full'>
                  <div className='w-[250px]'>
                    <Button
                      label={ t( 'New client' ) }
                      color='#0bb173'
                      colorHover='#088D5C'
                      icon={ <PlusIcon className='w-[22px] h-[22px] fill-white' /> }
                      onClick={ () => { NavigationService.toNewClient( navigate, false ); } }
                    />
                  </div>
                </div>
                :
                null
            }
            {
              !isLoading ?
                <div className='flex flex-wrap justify-center w-full h-full'>
                  {
                    clients.length > 0 ?
                      clients.map( client => {
                        return (
                          <ClientComponent
                            key={ client.id }
                            client={ client }
                            t={ t }
                          />
                        );
                      } )
                      :
                      <div className='flex flex-col gap-4 w-full h-full pt-10 items-center justify-center'>
                        <ClientsIcon className='h-24'/>
                        <div className='flex flex-col items-center justify-center'>
                          <span className='font-bold'>{t( 'common:oops' ) }</span>
                          <span className='font-medium'>{ t( 'No clients found !' ) }</span>
                        </div>
                      </div>
                  }
                </div>
                :
                <Loading/>
            }
          </div>
        </div>
      </div>
    </>
  );

}